import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Text } from 'grommet';
import axios from '../../helpers/axios';
import { BasicLoader } from '../../helpers/Loaders';
import { analytics } from '../../services/analytics';
import useZendeskWidget from '../../helpers/useZendeskWidget';
import { formatDownloadTitle } from '@helpers/utils/formatUtils';

import { UserContext } from '../../services/Session';

import useEntitlements from '@hooks/monetization/useEntitlements';
import {
  ViewerEntitlementStatus,
  PaywallPageContext,
} from '../../components/Paywall/paywall.types';
import { shd } from '@shd/jslib/models';

import { HitScopeFeedback } from './components/HitScopeHeader';
import { NoHitScope } from './components/HitScopeVideoLoading';
import HitScopeClipList from './components/HitScopeCliplist';
import HitScopeFilters from './components/HitScopeFilters';
import HitScopeVideoPlayer from './components/HitScopeVideoPlayer';
import HitScopeDownloadButton from './components/HitScopeDownloadButton';
import useHitScope from './useHitscope';
import { HitScopeClip, Hit, TeamsHitScopeProps } from './hitScope.types';
import { usePaywallContext } from '@contexts/PaywallContext';

const TeamsHitScope: React.FC<TeamsHitScopeProps> = ({
  teamIdsString: propTeamIds,
}) => {
  const { teamids: urlTeamIds } = useParams<{ teamids: string }>();
  const teamIds = urlTeamIds || propTeamIds;
  const [teams, setTeams] = useState<{ info: shd.Team; players: shd.Player }[]>(
    []
  );
  const [manualSelectedClip, setManualSelectedClip] = useState<
    HitScopeClip | undefined
  >();
  const [currentClip, setCurrentClip] = useState<HitScopeClip>();
  const [endAtTs, setEndAtTs] = useState(0);
  const [hitType, setHitType] = useState<Hit>(Hit.HITS);

  useZendeskWidget();
  const userStore = useContext(UserContext);
  const { fetchClips, clips, m3u8Text, selectedClip, loading, error } =
    useHitScope({
      type: 'team',
    });

  const { entitlementStatus, loading: entitlementLoading } = useEntitlements({
    teamId: teamIds,
    sport: 'bbsb',
  });

  const { renderLockOrCTA } = usePaywallContext({ entitlementStatus });

  const onClipChanged = (newClip: HitScopeClip) => {
    setCurrentClip(newClip);
  };

  useEffect(() => {
    setCurrentClip(selectedClip);
  }, [selectedClip]);

  useEffect(() => {
    fetchClips({
      endAtTs,
      good: hitType === Hit.HITS,
      qType: hitType,
      teamIds,
    });
  }, [fetchClips, endAtTs, hitType, teamIds]);

  useEffect(() => {
    const getTeams = async () => {
      const localTeams = [];
      for (const teamId of teamIds.split(',')) {
        // eslint-disable-next-line no-await-in-loop
        const res = await axios.get(`/api/team/${teamId}`);
        localTeams.push(res.data);
      }
      setTeams(localTeams);
    };
    getTeams();
  }, [endAtTs, teamIds]);

  useEffect(() => {
    analytics.track('HitScope View', {
      type: 'Team',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!teamIds) {
    return <BasicLoader size={200} fullPage />;
  }

  return (
    <Box gap="large" width="xlarge" margin="0 auto" pad="medium">
      {entitlementLoading ? (
        <BasicLoader />
      ) : (
        <>
          {entitlementStatus !== ViewerEntitlementStatus.ENTITLED &&
            renderLockOrCTA({
              entitlementStatus,
              isLoggedIn: !!userStore.authUser,
              isPrivate:
                entitlementStatus ===
                ViewerEntitlementStatus.ENTITLED_BUT_PRIVATE,
              mode: 'hitscope',
              context: PaywallPageContext.TEAM_HITSCOPE,
            })}
          {entitlementStatus === ViewerEntitlementStatus.ENTITLED && (
            <>
              {clips.length >= 1 ? (
                <>
                  <HitScopeVideoPlayer
                    clips={clips}
                    m3u8Text={m3u8Text}
                    onClipChanged={onClipChanged}
                    seekToClip={manualSelectedClip}
                  />
                  <Box direction="row" justify="between">
                    <HitScopeDownloadButton
                      numberOfClips={clips.length}
                      title={formatDownloadTitle(
                        `hitscope-${teams[0]?.info?.nameLong}`
                      )}
                      type={'team'}
                      downloadRequest={{
                        endAtTs,
                        good: hitType === Hit.HITS,
                        qType: hitType,
                        teamIds,
                      }}
                    />
                  </Box>
                  <HitScopeFeedback />
                  <HitScopeFilters
                    setEndAtTs={setEndAtTs}
                    setHitType={setHitType}
                    loading={loading}
                    track={{ type: 'team', teamId: teamIds }}
                  />
                  {clips && selectedClip && (
                    <HitScopeClipList
                      track={{
                        type: 'team',
                        teamId: teamIds,
                      }}
                      clips={clips}
                      setManualSelectedClip={setManualSelectedClip}
                      selectedClip={currentClip}
                      loading={loading}
                    />
                  )}
                  {error && <Text color="red">{error}</Text>}
                </>
              ) : (
                <NoHitScope />
              )}
            </>
          )}
        </>
      )}
    </Box>
  );
};

export default TeamsHitScope;
